import React, { useEffect, useRef } from 'react';
import {
  getInlineFieldDefaultShellClasses,
  getInlineFieldSmallShellClasses,
  mergeInlineFieldClassNames,
} from './inline-field-shell';

export type InlineEditProps<T> = {
  /** When true, show formatted value only (no interactive control). */
  readOnly: boolean;
  value: T;
  onChange: (value: T) => void;
  renderDisplay: (value: T) => React.ReactNode;
  renderEditor: (
    value: T,
    onChange: (value: T) => void,
    onBlurCommit: (currentValue?: T) => void
  ) => React.ReactNode;
  /** Matches compact editors (`Input small`, datepicker-small, inline select). */
  small?: boolean;
  className?: string;
  onBlur?: (value: T) => void;
};

export function InlineEdit<T>({
  readOnly,
  value,
  onChange,
  renderDisplay,
  renderEditor,
  small = true,
  className,
  onBlur,
}: InlineEditProps<T>) {
  const currentValueRef = useRef<T>(value);

  useEffect(() => {
    currentValueRef.current = value;
  }, [value]);

  const handleChange = (newValue: T) => {
    currentValueRef.current = newValue;
    onChange(newValue);
  };

  const onBlurCommit = (explicit?: T) => {
    if (onBlur) {
      onBlur(explicit !== undefined ? explicit : currentValueRef.current);
    }
  };

  const readOnlyInnerClassName = mergeInlineFieldClassNames(
    'tw:w-full tw:text-left tw:!rounded-lg',
    small ? getInlineFieldSmallShellClasses() : getInlineFieldDefaultShellClasses(),
    readOnly ? 'tw:px-2 tw:py-1' : undefined
  );

  if (readOnly) {
    return (
      <div className={mergeInlineFieldClassNames(className, 'tw:w-full')}>
        <div className={readOnlyInnerClassName}>{renderDisplay(value)}</div>
      </div>
    );
  }

  return <div className={className}>{renderEditor(value, handleChange, onBlurCommit)}</div>;
}
